<script lang="ts">
  import type { User } from "@/src/types/users";
  import type { Operator } from "@/src/types/components/operators/Operator";
  import { isAuthenticated } from "$lib/services/store.js";
  import {
    registerUser,
    startTournamentGame,
    getSessionData,
    fetchRunningTournament,
    playerResults,
  } from "$lib/services/tournaments/endpoints";
  import { page } from "$app/stores";
  import { user, isUserVerified } from "$lib/services/store";
  import { onDestroy, onMount } from "svelte";
  import { getOperator } from "$lib/services/tournaments/getOperator";
  import { getToplistOperators } from "$lib/services/tournaments/getToplist";
  import { generateUserHash } from "$lib/services/tournaments/hashGen";
  import Breadcrumbs from "$lib/main/components/Breadcrumbs.svelte";
  import UserStatistics from "./UserStatistics.svelte";
  import DataTable from "../DataTable/DataTable.svelte";
  import GameIframe from "./gameIframe.svelte";
  import VerifyAccountCard from "$lib/admin/components/blocks/blocks/Tournaments/VerifyAccountCard.svelte";
  import FAQ from "$lib/admin/components/blocks/blocks/FAQ.svelte";
  import { getTournamentsPageData } from "../DataTable/dataFetcher";
  import AboutTournamentProvider from "./AboutTournamentProvider.svelte";
  import TournamentToplist from "./TournamentToplist/Block.svelte";

  $: username = ($user as User | null)?.username ?? "";
  $: userId = ($user as User | null)?.id ?? 0;

  let gameUrl: string = "";
  let loading: boolean = true;
  let errorMessage: string = "";
  let sessionData: any = null;
  let tournamentData: any = null;
  let spinsLimit: number = 0;
  let userStats = {
    score: 0,
    rank: 0,
    spinsLeft: 0,
    spinsTotal: 0,
  };
  let faq;
  let tournamentsTabs;
  let aboutTournamentProvider;

  export let tournamentId: number;
  export let tournamentName: string;
  export let sponsoredBy: number;
  export let tournamentToplist: number;
  export let tournamentIsLive: boolean;
  export let tournamentAsset: string;
  export let userTournamentStatus: string;

  let topListMaximumOperators = 3;
  let operator: Operator | null = null;
  let toplistOperators: (Operator | null)[] = [];
  let isInitializing: boolean = false;

  const initializeGame = async () => {
    if (isInitializing) return;
    isInitializing = true;

    try {
      // Handle user registation if needed
      if (userTournamentStatus === "register") {
        const hash = generateUserHash(username, userId, "casinofeber");
        await registerUser(username, userId, hash);

        // Update status after successful registration
        // Commented for now to test how the startGame behaves in production
        // TODO: Remove this from the code if the behaviour is consistent
        // userTournamentStatus = "join";
      }

      // Start or resume game
      if (userTournamentStatus === "register" || userTournamentStatus === "resume" || userTournamentStatus === "join") {
        const response = await startTournamentGame(tournamentId, username);
        if (response?.data?.gameURL) {
          gameUrl = response.data.gameURL;
        }
      }

      // Now that we're registered and have started the game, get session data
      try {
        sessionData = await getSessionData(tournamentId, username);

        if (sessionData?.data) {
          spinsLimit = sessionData.data.spinsLimit || 50;

          // Get latest player results
          if (sessionData.data.apiURL && sessionData.data.token) {
            try {
              const results = await playerResults(sessionData.data.apiURL, sessionData.data.token);
              userStats = {
                score: results.score || 0,
                rank: results.position || 0,
                spinsLeft: spinsLimit - (results.spins_taken || 0),
                spinsTotal: spinsLimit,
              };
            } catch (error) {
              console.error("Error fetching player results:", error);
              userStats = {
                ...userStats,
                spinsLeft: spinsLimit,
                spinsTotal: spinsLimit,
              };
            }
          }
        }
      } catch (error) {
        console.error("Error getting session data, will try to get stats from tournament data");
      }

      // Get tournament data for more info
      try {
        tournamentData = await fetchRunningTournament(tournamentId, username);

        if (tournamentData?.gameUrl && !gameUrl) {
          gameUrl = tournamentData.gameUrl;
        }

        // Only update stats if we didn't get them from playerResults
        if (!sessionData?.data?.apiURL) {
          userStats = {
            score: tournamentData.playerScoreNoPrefix || 0,
            rank: tournamentData.userPosition || 0,
            spinsLeft: tournamentData.spinsLeft || spinsLimit,
            spinsTotal: spinsLimit,
          };
        }
      } catch (error) {
        console.error("Error fetching tournament data:", error);
        tournamentData = null;
      }

      loading = false;
      errorMessage = "";
    } catch (error: any) {
      console.error("Failed to initialize game:", error);
      errorMessage = error?.message || "Failed to initialize game session. Please try again later.";
      loading = false;
    } finally {
      isInitializing = false;
    }
  };

  const handleGameUpdate = (event: CustomEvent) => {
    const results = event.detail;
    if (results) {
      userStats = {
        score: results.score || userStats.score,
        rank: results.position || userStats.rank,
        spinsLeft: results.spinsLeft || userStats.spinsLeft,
        spinsTotal: spinsLimit,
      };
    }
  };

  $: iframeProps = {
    gameUrl,
    tournamentId,
    username,
    spinsLimit,
  };

  const leaderboardProps = {
    tabs: [
      {
        title: "Leaderboard",
        isOpen: true,
        endpoint: "leaderboard",
        additionalArgs: {
          tournamentId: tournamentId,
        },
        selectedColumns: [
          {
            key: "position",
            displayName: "",
          },
          {
            key: "playerName",
            displayName: "Name",
          },
          {
            key: "playerScore",
            displayName: "Score",
          },
          {
            key: "displayPrize",
            displayName: "Prize",
          },
        ],
        endpointSelected: true,
      },
    ],
    hideHeader: false,
    isAccordion: true,
  };

  onMount(async () => {
    faq = await getTournamentsPageData("FAQ");
    tournamentsTabs = await getTournamentsPageData("DataTable");
    aboutTournamentProvider = await getTournamentsPageData("AboutTournamentProvider");

    if ($isAuthenticated) {
      initializeGame();
    }
    window.addEventListener("playerStatsUpdate", handleGameUpdate as EventListener);
    getOperator(sponsoredBy, tournamentIsLive).then((data) => (operator = data));
    getToplistOperators(tournamentToplist, tournamentIsLive, topListMaximumOperators).then(
      (data) => (toplistOperators = data),
    );
  });

  onDestroy(() => {
    window.removeEventListener("playerStatsUpdate", handleGameUpdate as EventListener);
  });

  $: if (tournamentId) {
    getOperator(sponsoredBy, tournamentIsLive).then((data) => (operator = data));
    getToplistOperators(tournamentToplist, tournamentIsLive, topListMaximumOperators).then(
      (data) => (toplistOperators = data),
    );
  }
</script>

<Breadcrumbs path={$page.url.pathname} hasDarkMode={true} />

<div class="content-grid mt-6">
  <div class="tournamentPage-header">
    <div class="user-statistics">
      <UserStatistics operatorId={sponsoredBy} {tournamentName} {operator} stats={userStats} />
    </div>
    <div class="leaderboard-active-tournament" data-element="leaderboard_click">
      <DataTable props={leaderboardProps} />
    </div>
  </div>
  {#if $isUserVerified && $user.marketingConsent && $user.messageConsent}
    {#if loading}
      <p>Spelet laddas…...</p>
    {:else if errorMessage}
      <p>Error: {errorMessage}</p>
    {:else if gameUrl}
      <GameIframe {...iframeProps} />
    {/if}
  {:else}
    <VerifyAccountCard tournamentImage={tournamentAsset} />
  {/if}
</div>

<TournamentToplist toplistOperators={toplistOperators} props={{ toplistId: tournamentToplist }}/>

{#if tournamentsTabs && tournamentsTabs.props}
  <DataTable props={tournamentsTabs.props} />
{/if}

{#if faq && faq.props && faq.props.content}
  <FAQ props={faq.props} />
{/if}

{#if aboutTournamentProvider && aboutTournamentProvider.props}
  <AboutTournamentProvider props={aboutTournamentProvider.props} />
{/if}

<style>
  .tournamentPage-header {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 3fr;
    gap: 20px;

    grid-template-areas:
      "user-statistics"
      "leaderboard";
  }
  @media only screen and (min-width: 992px) {
    .tournamentPage-header {
      grid-template-areas: "user-statistics leaderboard";
      grid-template-columns: 2fr 1fr;
    }
  }
  .user-statistics {
    grid-area: user-statistics;
  }
  .leaderboard-active-tournament {
    grid-area: leaderboard;
    margin-bottom: 20px;
  }
  :global(.tournamentPage-header .leaderboard-active-tournament .accordion) {
    /* Necessary for the tooltip in the game page leaderboard */
    overflow: visible;
  }
  @media only screen and (min-width: 992px) {
    .leaderboard-active-tournament {
      margin-bottom: 10px;
    }
  }

  :global(.leaderboard-active-tournament .accordion .accordion-item) {
    margin-bottom: 0;
  }

  :global(.leaderboard-active-tournament .accordion-content) {
    height: 226px;
    overflow-y: scroll;
    font-size: 14px;
    font-weight: 400;
  }
  @media only screen and (min-width: 992px) {
    :global(.leaderboard-active-tournament .accordion-content) {
      height: 130px;
    }
  }

  :global(.leaderboard-active-tournament .accordion-content::-webkit-scrollbar) {
    width: 10px;
  }
  :global(.leaderboard-active-tournament .accordion-content::-webkit-scrollbar-track) {
    background: #e0e0e0;
    border-radius: 5px;
  }
  :global(.leaderboard-active-tournament .accordion-content::-webkit-scrollbar-thumb) {
    background: #cc3129;
    border-radius: 5px;
  }
  :global(.leaderboard-active-tournament .accordion-content::-webkit-scrollbar-thumb:hover) {
    background: #9f2620;
  }
</style>
